import { concat, get } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import join from '@nullvoxpopuli/ember-composable-helpers/helpers/join';
import dayjs from 'dayjs';
import type { EmberChangeset } from 'ember-changeset';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import {
  validateDate,
  validatePresence
} from 'ember-changeset-validations/validators';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import type { ValidatorMap } from 'ember-changeset/types';
import type Store from 'ember-data/store';
import { t, type IntlService } from 'ember-intl';
import eq from 'ember-truth-helpers/helpers/eq';
import WrForm from 'ember-wiser/components/form';
import type Patient from 'parog-web/models/patient';
import PatientDisenrolledJournalEvent, {
  proIrcReasons,
  reasons
} from 'parog-web/models/patient-disenrolled-journal-event';
import type FetcherService from 'parog-web/services/fetcher';
import TypedEmberChangeset from 'parog-web/utils/typed-ember-changeset';
import validateSometimes from 'parog-web/validators/sometimes';
import Dropdown from 'ui-kit/components/dropdown';

interface Signature {
  Args: {
    patient: Patient;
  };
}

/* eslint-disable ember/no-get, ember/classic-decorator-no-classic-methods */
class DisenrollmentChangeset extends TypedEmberChangeset<PatientDisenrolledJournalEvent> {
  get reasonDate() {
    const overridden = this.get('_reasonDate');
    if (overridden !== undefined) {
      return overridden;
    }

    return this.get('reason') === 'patient-death'
      ? this.get('occurredAt')
      : this.get('reasonEventOn');
  }

  set reasonDate(value: Date) {
    this.set('_reasonDate', value);
  }

  override async save() {
    if (this.get('reason') == 'patient-death') {
      this.set('reasonEventOn', null);
      this.set('occurredAt', this._reasonDate ?? this.get('occurredAt'));
    } else {
      this.set('occurredAt', new Date());
      this.set('reasonEventOn', this._reasonDate ?? this.get('reasonEventOn'));
    }
    await super.save();
  }
}
/* eslint-enable ember/no-get, ember/classic-decorator-no-classic-methods */

export default class DisenrollmentForm extends Component<Signature> {
  @service declare fetcher: FetcherService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: Store;

  changeset: EmberChangeset;

  get reasonOptions() {
    if (this.args.patient.healthCentre.proIrc) {
      return [...reasons, ...proIrcReasons];
    }
    return reasons;
  }

  get #model(): PatientDisenrolledJournalEvent {
    if (!this.#retrievedJournalEvent) {
      return this.store.createRecord('patient-disenrolled-journal-event', {
        occurredAt: new Date(),
        reasonEventOn: new Date()
      });
    }

    return this.#retrievedJournalEvent;
  }

  get #retrievedJournalEvent(): PatientDisenrolledJournalEvent | undefined {
    return this.args.patient.patientDisenrolledJournalEvent;
  }

  get #validations(): ValidatorMap {
    const validations = PatientDisenrolledJournalEvent.validations(this.intl);

    validations['deathDate'] = [
      validateSometimes(
        validatePresence({
          presence: true,
          message: 'La date ne peut être vide'
        }),
        function (this: EmberChangeset) {
          return (this as EmberChangeset).get('reason') == 'patient-death';
        }
      ),

      validateSometimes(
        validateDate({
          message:
            'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
          onOrAfter: new Date('01/01/1900')
        }),
        function (this: EmberChangeset) {
          return (this as EmberChangeset).get('reason') == 'patient-death';
        }
      ),

      validateSometimes(
        validateDate({
          message:
            'Cette donnée ne peut être déclarée dans le futur par rapport à la date de désinscription précédente. Veuillez modifier la date',
          onOrBefore:
            this.#retrievedJournalEvent?.occurredAt ??
            dayjs().endOf('day').toDate()
        }),
        function (this: EmberChangeset) {
          return (this as EmberChangeset).get('reason') == 'patient-death';
        }
      )
    ];

    return validations;
  }

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    this.changeset = Changeset(
      this.#model,
      lookupValidator(this.#validations),
      this.#validations,
      { changeset: DisenrollmentChangeset }
    );
  }

  clearDeathDateError = () => {
    this.changeset.removeError('deathDate');
  };

  onCancelDisenrollment = () => {
    this.router.transitionTo(
      'medics-web.protected.patients.patient.administrative'
    );
  };

  onSubmit = async () => {
    if (!this.#retrievedJournalEvent) {
      this.changeset.set('patient', this.args.patient);
    }
    this.changeset.set('deathDate', this.changeset.reasonDate);

    await this.changeset.validate();
    if (this.changeset.isValid) {
      await this.changeset.execute();
      await this.changeset.save();
      await this.#afterCompleted();
    }
  };

  updateReason = (value: string | string[]) => {
    this.changeset.set('reason', value);

    this.clearDeathDateError();
  };

  #afterCompleted = async () => {
    this.router.transitionTo('medics-web.protected.patients.patient.dashboard');
  };

  <template>
    <WrForm @data={{this.changeset}} @onSubmit={{this.onSubmit}} as |form|>
      <div class='text-sans pb-4'>En désinscrivant votre patient de MedicWise,
        ses données ne seront plus collectées et il ne bénéficiera plus d'aucun
        suivi au sein de la plateforme.</div>
      <form.Row aria-label='reason'>
        <:header>Sélectionner un motif *</:header>
        <:col1>
          <Dropdown
            @isMandatory={{true}}
            @placeholder='Sélectionner un motif'
            @selected={{changesetGet this.changeset 'reason'}}
            @onChange={{this.updateReason}}
            as |dropdown|
          >
            {{#each this.reasonOptions as |reasonOption|}}
              <dropdown.Item
                @key={{reasonOption}}
                @value={{t
                  (concat
                    'patient-disenrolled-journal-event.reasons.' reasonOption
                  )
                }}
              />
            {{/each}}
          </Dropdown>
          {{#if
            (get (changesetGet this.changeset 'error.reason') 'validation')
          }}
            <div
              class='text-red pt-2 leading-[1.094rem] sm:pt-0'
              aria-label='reasonError'
            >{{get
                (changesetGet this.changeset 'error.reason')
                'validation'
              }}</div>

          {{/if}}
        </:col1>

      </form.Row>

      <form.InputRow
        @name='reasonDate'
        @label={{if (eq this.changeset.reason 'patient-death') 'Date *' 'Date'}}
        type='date'
        class='[&+span]:block'
        {{on 'input' this.clearDeathDateError}}
      >
        {{#if
          (get (changesetGet this.changeset 'error.deathDate') 'validation')
        }}
          <div
            class='text-red leading-[1.094rem]'
            aria-label='deathDateError'
          >{{join
              '. '
              (get (changesetGet this.changeset 'error.deathDate') 'validation')
            }}</div>
        {{/if}}
      </form.InputRow>

      <form.TextareaRow
        @label='Informations complémentaires'
        @name='comments'
        class='mt-2 sm:mt-0'
      />

      <div class='flex justify-end gap-2 pt-4'>
        <form.CancelButton
          {{on 'click' this.onCancelDisenrollment}}
        >Fermer</form.CancelButton>
        <form.SubmitButton @icon='Tick'>Valider</form.SubmitButton>
      </div>

    </WrForm>
  </template>
}
